'use client';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useLogger } from 'next-axiom';
import { useEffect } from 'react';
import { isWishlistEnabled } from '../../server/wishlist-utils';
import { getSecondsUntilExpiry, isTokenExpired } from '../jwt-utils';
import {
  WISHLIST_API_PATHS,
  WISHLIST_CACHE_CONFIG,
  WISHLIST_QUERY_KEYS,
} from '../wishlist-constants';

// Track retry attempts to prevent infinite loops
let retryCount = 0;
const MAX_RETRIES = 3;

async function fetchSessionToken(forceRefresh = false): Promise<string | null> {
  try {
    const headers: Record<string, string> = {
      'X-Retry-Count': retryCount.toString(),
    };

    // Only add force refresh header when explicitly requested
    if (forceRefresh) {
      headers['X-Force-Refresh'] = 'true';
    }

    const response = await fetch(
      `${WISHLIST_API_PATHS.BASE}${WISHLIST_API_PATHS.SESSION}`,
      {
        method: 'POST',
        headers,
      }
    );

    if (!response.ok) {
      // If we get a 400/500 error, it might be a server issue
      // Don't retry infinitely
      if (retryCount >= MAX_RETRIES) {
        retryCount = 0; // Reset for next time
        throw new Error(
          `Failed to fetch session token after ${MAX_RETRIES} retries`
        );
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    // Reset retry count on success
    retryCount = 0;

    return data.token || null;
  } catch (error) {
    // Increment retry count for tracking
    retryCount++;

    // Re-throw to trigger React Query's retry logic
    throw error;
  }
}

const MATCH_4XX_ERROR = /status: (\d+)/;

export function useWishlistSessionToken() {
  const enabled = isWishlistEnabled();
  const log = useLogger().with({ location: 'useWishlistSessionToken' });
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: [WISHLIST_QUERY_KEYS.SESSION_TOKEN],
    queryFn: () => fetchSessionToken(false), // Normal mode - don't force refresh
    enabled,
    staleTime: WISHLIST_CACHE_CONFIG.SESSION_TOKEN_STALE_TIME,
    gcTime: WISHLIST_CACHE_CONFIG.SESSION_TOKEN_GC_TIME,
    retry: (failureCount, error) => {
      // Limit retries to prevent infinite loops
      if (failureCount >= MAX_RETRIES) {
        log.error('Max retries reached for session token', {
          failureCount,
          error: error instanceof Error ? error.message : 'Unknown error',
        });
        return false;
      }

      // Don't retry on 4xx errors (except 401 which might be token-related)
      if (error instanceof Error && error.message.includes('status: 4')) {
        const status = error.message.match(MATCH_4XX_ERROR)?.[1];
        if (status && status !== '401' && status !== '403') {
          log.error('Not retrying due to client error', { status });
          return false;
        }
      }

      return true;
    },
    retryDelay: (attemptIndex) => {
      // Exponential backoff: 1s, 2s, 4s
      return Math.min(1000 * 2 ** attemptIndex, 4000);
    },
  });

  // Set up proactive token renewal
  useEffect(() => {
    if (!query.data || !enabled) return;

    const token = query.data;

    // Check if token needs renewal
    if (isTokenExpired(token)) {
      if (process.env.NODE_ENV === 'production') {
        // Token is expired or expiring soon, triggering renewal
      }
      queryClient.invalidateQueries({
        queryKey: [WISHLIST_QUERY_KEYS.SESSION_TOKEN],
      });
      return;
    }

    // Calculate when to renew (1 hour before expiry)
    const secondsUntilExpiry = getSecondsUntilExpiry(token);
    const bufferSeconds = 3600; // 1 hour buffer
    const renewInSeconds = Math.max(0, secondsUntilExpiry - bufferSeconds);

    if (renewInSeconds > 0) {
      const timeoutId = setTimeout(() => {
        if (process.env.NODE_ENV === 'production') {
          // Proactively renewing token before expiry
        }
        queryClient.invalidateQueries({
          queryKey: [WISHLIST_QUERY_KEYS.SESSION_TOKEN],
        });
      }, renewInSeconds * 1000);

      return () => clearTimeout(timeoutId);
    }
  }, [query.data, enabled, queryClient]);

  // Return null if token is expired on client side
  const isExpired = query.data ? isTokenExpired(query.data, 0) : false;

  // Custom refetch function that supports forced refresh
  const refetch = async (options?: { forceRefresh?: boolean }) => {
    const forceRefresh = options?.forceRefresh ?? false;

    if (forceRefresh) {
      // Clear existing cache and force new token
      queryClient.setQueryData([WISHLIST_QUERY_KEYS.SESSION_TOKEN], null);
      try {
        const data = await queryClient.fetchQuery({
          queryKey: [WISHLIST_QUERY_KEYS.SESSION_TOKEN],
          queryFn: () => fetchSessionToken(true), // Force refresh mode
          staleTime: 0, // Force fresh fetch
        });

        // Return in the same format as query.refetch()
        return { data, error: null, isError: false, isSuccess: true };
      } catch (error) {
        return {
          data: null,
          error: error as Error,
          isError: true,
          isSuccess: false,
        };
      }
    }

    return query.refetch();
  };

  return {
    ...query,
    data: isExpired ? null : query.data,
    refetch,
  };
}
